import { useMemo } from 'react';
import { View, type ViewProps } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import {
  WorkoutCompletionStatus,
  WorkoutSummaryUtils,
  WorkoutUtils,
} from '@fytness/shared';
import { Icon } from '~/global/components/icon/Icon';
import type { IconProps } from '~/global/components/icon/Icon';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { PlannedDateRange } from '@/lib/local-api/workout/planned-date-range';
import type {
  WorkoutRecord,
  WorkoutSetRecord,
  WorkoutSetValueRecord,
  WorkoutExerciseDimensionRecord,
  RecordedSessionRecord,
} from '~/powersync/generated/schema';

interface StatCardProps {
  icon: IconProps['name'];
  value: string;
  label: string;
}

const StatCard = ({ icon, value, label }: StatCardProps) => (
  <View className="flex-1 rounded-xl bg-surface p-4 gap-2">
    <View className="h-9 w-9 rounded-lg bg-surface-elevated items-center justify-center">
      <Icon name={icon} size={18} className="text-accent" />
    </View>
    <Text
      className="font-sora-bold text-2xl text-text-primary"
      style={{ letterSpacing: -1 }}
    >
      {value}
    </Text>
    <Text className="font-inter-medium text-xs text-text-muted">{label}</Text>
  </View>
);

export interface WorkoutStatsSectionProps extends ViewProps {}

export const WorkoutStatsSection = ({
  className,
  ...props
}: WorkoutStatsSectionProps) => {
  const { t } = useTranslation('workout');

  // `plannedAt` is a local calendar date; compare it as one so this-week workouts
  // aren't dropped for users behind UTC (see PlannedDateRange).
  const { fromDate, toDate } = PlannedDateRange.forWeekToDate(new Date());

  const { data: workouts } = useLivePowerSyncQuery<
    WorkoutRecord & { exerciseCount: number }
  >(db.Workout.queries.findByDateRange(fromDate, toDate), [fromDate, toDate]);

  // Only finished sessions contribute to the weekly summary.
  const finishedWorkouts = useMemo(
    () =>
      workouts.filter(
        (w) =>
          WorkoutUtils.getCompletionStatus(w) ===
          WorkoutCompletionStatus.FINISHED,
      ),
    [workouts],
  );

  const workoutIds = useMemo(
    () => finishedWorkouts.map((w) => w.id),
    [finishedWorkouts],
  );
  const workoutIdsKey = workoutIds.join(',');

  const { data: setValues } = useLivePowerSyncQuery<WorkoutSetValueRecord>(
    db.Workout.queries.findSetValuesByWorkoutIds(workoutIds),
    [workoutIdsKey],
  );
  const { data: dimensions } =
    useLivePowerSyncQuery<WorkoutExerciseDimensionRecord>(
      db.Workout.queries.findDimensionsByWorkoutIds(workoutIds),
      [workoutIdsKey],
    );
  const { data: sets } = useLivePowerSyncQuery<WorkoutSetRecord>(
    db.Workout.queries.findSetsByWorkoutIds(workoutIds),
    [workoutIdsKey],
  );

  // Active calories come from device-recorded sessions attached to the week's
  // finished workouts (#29); summed for the weekly total.
  const { data: recordedSessions } =
    useLivePowerSyncQuery<RecordedSessionRecord>(
      db.RecordedSession.queries.findByWorkoutIds(workoutIds),
      [workoutIdsKey],
    );

  const totalCalories = useMemo(
    () =>
      recordedSessions.reduce(
        (acc, session) => acc + (session.activeCalories ?? 0),
        0,
      ),
    [recordedSessions],
  );

  const totalDurationMinutes = useMemo(
    () =>
      finishedWorkouts.reduce(
        (acc, w) =>
          acc +
          WorkoutSummaryUtils.calculateDuration({
            startedAt: w.startedAt ? new Date(w.startedAt) : null,
            finishedAt: w.finishedAt ? new Date(w.finishedAt) : null,
          }),
        0,
      ),
    [finishedWorkouts],
  );

  // Volume (weight × reps over completed sets) is set-grouped, so aggregating
  // every set across the week yields the correct weekly total.
  const totalVolume = useMemo(
    () =>
      WorkoutSummaryUtils.calculateSummary({
        workout: { id: 'weekly-aggregate', startedAt: null, finishedAt: null },
        setValues: setValues.map((v) => ({
          id: v.id,
          setId: v.setId ?? '',
          exerciseDimensionId: v.exerciseDimensionId ?? '',
          value: Number(v.value),
          workoutId: v.workoutId ?? '',
        })),
        exerciseDimensions: dimensions.map((d) => ({
          id: d.id,
          exerciseId: d.exerciseId ?? '',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dimension: d.dimension as any,
          unit: d.unit ?? '',
          workoutId: d.workoutId ?? '',
        })),
        sets: sets.map((s) => ({
          id: s.id,
          completedAt: s.completedAt ? new Date(s.completedAt) : null,
        })),
        exerciseIds: [],
      }).totalVolume,
    [setValues, dimensions, sets],
  );

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center mb-3 gap-3">
        <View className="h-0.5 w-6 rounded-sm bg-accent" />
        <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
          {t('stats.title')}
        </Text>
      </View>

      <View className="flex flex-col gap-3">
        <View className="flex flex-row gap-3">
          <StatCard
            icon="Dumbbell"
            value={String(finishedWorkouts.length)}
            label={t('stats.workouts')}
          />
          <StatCard
            icon="Timer"
            value={WorkoutSummaryUtils.formatDuration(totalDurationMinutes)}
            label={t('stats.totalTime')}
          />
        </View>
        <View className="flex flex-row gap-3">
          <StatCard
            icon="Flame"
            value={String(Math.round(totalCalories))}
            label={t('stats.caloriesBurned')}
          />
          <StatCard
            icon="Weight"
            value={WorkoutSummaryUtils.formatVolume(totalVolume)}
            label={t('stats.weightLifted')}
          />
        </View>
      </View>
    </View>
  );
};
